import React, { useEffect, useState, useMemo, useCallback, memo } from "react";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import api from "../Service/api";
import "./CustomerLedger.css";

// ---------- Helper functions ----------
const formatDateTime = (dateTime) => {
  if (!dateTime) return "--";
  const date = new Date(dateTime);
  return isNaN(date) ? "--" : date.toLocaleString();
};

const formatCurrency = (amount) => {
  if (amount === undefined || amount === null || isNaN(amount)) return "0.00";
  return Number(amount).toFixed(2);
};
const formatDateOnly = (dateTime) => {
  if (!dateTime) return "--";
  const date = new Date(dateTime);
  return isNaN(date) ? "--" : date.toLocaleDateString();
};

const calculateInvoiceTotal = (invoice) => {
  if (!invoice || typeof invoice !== "object") return 0;
  const beforeGst = Number(invoice.totalBeforeGst || 0);
  const discount = Number(invoice.discount || 0);
  const rent = Number(invoice.rent || 0);
  const gst = Number(invoice.gstAmount || 0);
  return beforeGst - discount + rent + gst;
};

const getTransactionImpact = (invoice) => {
  if (!invoice || typeof invoice !== "object") return 0;
  if (invoice.isPayment) {
    return -Number(invoice.amountPaid || 0);
  }
  const beforeGst = Number(invoice.totalBeforeGst || 0);
  const discount = Number(invoice.discount || 0);
  const rent = Number(invoice.rent || 0);
  const gst = Number(invoice.gstAmount || 0);
  const invoiceTotal = beforeGst - discount + rent + gst;
  const paid = Number(invoice.amountPaid || 0);
  let impact = invoice.status === "Purchase" ? invoiceTotal : -invoiceTotal;
  impact -= paid;
  return impact;
};

const filterZeroPayments = (invoices) => {
  if (!Array.isArray(invoices)) return [];
  return invoices.filter((inv) => {
    if (inv && inv.isPayment) {
      return inv.amountPaid && Number(inv.amountPaid) !== 0;
    }
    return !!inv;
  });
};

const getTransactionDate = (invoice) => {
  if (!invoice) return new Date(0);
  if (invoice.isPayment) {
    return invoice.paymentTime ? new Date(invoice.paymentTime) : new Date(0);
  }
  const dateStr = invoice.purchaseRecordTime || invoice.returnRecordTime;
  return dateStr ? new Date(dateStr) : new Date(0);
};

// ---- NEW helpers for product size/detail (used in print & Excel) ----
const getSizeDisplay = (product) => {
  const category = product?.category?.toLowerCase() || '';
  if (category === 'roll') return product.size || '---';
  if (category === 'reel' || category === 'tota') return product.length || '---';
  if (category === 'packet') return product.size || '---';
  return product.size || '---';
};

const getDetail = (product) => {
  const category = product?.category?.toLowerCase() || '';
  if (category === 'roll') return `Size: ${product.size}`;
  if (category === 'reel' || category === 'tota') return `Length: ${product.length}`;
  if (category === 'packet') {
    const parts = [];
    if (product.size) parts.push(`Size: ${product.size}`);
    if (product.ktae) parts.push(`KTAE: ${product.ktae}`);
    if (product.gram) parts.push(`Gram: ${product.gram}`);
    return parts.join(' | ');
  }
  return product.size ? `Size: ${product.size}` : '';
};

// ---------- Memoized MonthSection ----------
const MonthSection = memo(({ monthData, expandedInvoices, onToggleInvoice }) => {
  if (!monthData || typeof monthData !== "object") {
    return <div style={{ padding: "20px", color: "#999" }}>Invalid month data</div>;
  }

  const transactions = useMemo(() => {
    const filtered = filterZeroPayments(monthData.invoices);
    const sorted = [...filtered].sort((a, b) => {
      const dateA = getTransactionDate(a);
      const dateB = getTransactionDate(b);
      if (dateA - dateB !== 0) return dateA - dateB;
      if (a.isPayment && !b.isPayment) return 1;
      if (!a.isPayment && b.isPayment) return -1;
      return (parseInt(a.invoiceNumber) || 0) - (parseInt(b.invoiceNumber) || 0);
    });
    let running = Number(monthData.monthStartBalance || 0);
    return sorted.map((inv) => {
      const impact = getTransactionImpact(inv);
      const balanceAfter = running + impact;
      running = balanceAfter;
      return { invoice: inv, balanceAfter: isNaN(balanceAfter) ? 0 : balanceAfter };
    });
  }, [monthData]);

  const itemCount = transactions.length;
  const expandedSet = expandedInvoices instanceof Set ? expandedInvoices : new Set();

  const Row = useCallback(
    ({ index, style }) => {
      try {
        const item = transactions[index];
        if (!item) return <div style={style}>Missing item</div>;
        const { invoice, balanceAfter } = item;
        if (!invoice) return <div style={style}>Missing invoice</div>;

        const isExpanded = expandedSet.has(invoice?.invoiceNumber);
        const isPayment = invoice?.isPayment === true;
        const invoiceTotal = calculateInvoiceTotal(invoice);

        return (
          <div
            style={{
              ...style,
              borderBottom: "1px solid #f0f0f0",
              padding: "10px 20px",
              background: isPayment ? "#f0fff4" : index % 2 === 0 ? "#fff" : "#fafafa",
            }}
          >
            {/* Left side */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  {isPayment ? (
                    <>
                      <span style={{ fontSize: "16px", fontWeight: "bold", color: "#28a745" }}>
                        Payment
                      </span>
                      <span
                        style={{
                          background: "#d4edda",
                          color: "#155724",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "bold",
                        }}
                      >
                        PAYMENT
                      </span>
                    </>
                  ) : (
                    <>
                      <span
                        style={{
                          fontSize: "16px",
                          fontWeight: "bold",
                          color: invoice?.status === "Purchase" ? "#28a745" : "#dc3545",
                        }}
                      >
                        Invoice #{invoice?.invoiceNumber || "N/A"}
                      </span>
                      <span
                        style={{
                          background: invoice?.status === "Purchase" ? "#d4edda" : "#f8d7da",
                          color: invoice?.status === "Purchase" ? "#155724" : "#721c24",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "bold",
                        }}
                      >
                        {invoice?.status || "Unknown"}
                      </span>
                    </>
                  )}
                </div>
                {!isPayment && (
                  <div style={{ fontSize: "12px", color: "#6c757d", marginTop: "5px" }}>
                    <span style={{ marginRight: "15px" }}>
                      <i className="bi bi-calendar" style={{ marginRight: "3px" }}></i>Purchase:{" "}
                      {formatDateTime(invoice?.purchaseRecordTime)}
                    </span>
                    <span>
                      <i className="bi bi-arrow-return-left" style={{ marginRight: "3px" }}></i>Return:{" "}
                      {formatDateTime(invoice?.returnRecordTime)}
                    </span>
                  </div>
                )}
                {isPayment && (
                  <div style={{ fontSize: "12px", color: "#6c757d", marginTop: "5px" }}>
                    <i className="bi bi-clock" style={{ marginRight: "3px" }}></i>Payment Time:{" "}
                    {formatDateTime(invoice?.paymentTime)}
                  </div>
                )}
              </div>

              {/* Right side */}
              <div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
                {!isPayment && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "3px",
                      alignItems: "flex-end",
                      backgroundColor: "#f8f9fa",
                      padding: "8px 12px",
                      borderRadius: "6px",
                      border: "1px solid #e0e0e0",
                      minWidth: "250px",
                    }}
                  >
                    <div style={{ fontSize: "11px", color: "#495057", fontWeight: "bold" }}>
                      Invoice Breakdown
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#6c757d",
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "8px",
                      }}
                    >
                      <span>Subtotal:</span>
                      <strong style={{ color: "#20c997", textAlign: "right" }}>
                        {formatCurrency(invoice?.totalBeforeGst)}
                      </strong>
                      <span>Discount:</span>
                      <strong style={{ color: "#ffc107", textAlign: "right" }}>
                        - {formatCurrency(invoice?.discount || 0)}
                      </strong>
                      <span>Rent:</span>
                      <strong style={{ color: "#fd7e14", textAlign: "right" }}>
                        + {formatCurrency(invoice?.rent || 0)}
                      </strong>
                      <span>Before GST:</span>
                      <strong style={{ color: "#6f42c1", textAlign: "right" }}>
                        {formatCurrency(invoice?.totalBeforeGst || 0)}
                      </strong>
                      <span>GST ({invoice?.gstPercentage || 0}%):</span>
                      <strong style={{ color: "#dc3545", textAlign: "right" }}>
                        {formatCurrency(invoice?.gstAmount)}
                      </strong>
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "bold",
                        borderTop: "1px solid #dee2e6",
                        paddingTop: "4px",
                        width: "100%",
                        textAlign: "right",
                      }}
                    >
                      Total: {formatCurrency(invoiceTotal)}
                    </div>
                  </div>
                )}
                {!isPayment && invoice?.amountPaid > 0 && (
                  <div
                    style={{
                      textAlign: "right",
                      backgroundColor: "#e8f4ff",
                      padding: "8px 12px",
                      borderRadius: "6px",
                      border: "1px solid #99ccff",
                      minWidth: "120px",
                    }}
                  >
                    <div style={{ fontSize: "11px", color: "#0066cc" }}>
                      <i className="bi bi-cash-stack" style={{ marginRight: "3px" }}></i>Paid
                    </div>
                    <div style={{ fontSize: "16px", fontWeight: "bold", color: "#0066cc" }}>
                      {formatCurrency(invoice?.amountPaid)}
                    </div>
                  </div>
                )}
                {isPayment && (
                  <div
                    style={{
                      textAlign: "right",
                      backgroundColor: "#d4edda",
                      padding: "8px 12px",
                      borderRadius: "6px",
                      border: "1px solid #a3cfbb",
                      minWidth: "120px",
                    }}
                  >
                    <div style={{ fontSize: "11px", color: "#155724" }}>
                      <i className="bi bi-cash" style={{ marginRight: "3px" }}></i>Payment
                    </div>
                    <div style={{ fontSize: "16px", fontWeight: "bold", color: "#155724" }}>
                      {formatCurrency(invoice?.amountPaid)}
                    </div>
                  </div>
                )}
                <div
                  style={{
                    textAlign: "right",
                    backgroundColor: "#fff3cd",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid #ffeeba",
                    minWidth: "140px",
                  }}
                >
                  <div style={{ fontSize: "11px", color: "#856404" }}>
                    <i className="bi bi-wallet2" style={{ marginRight: "3px" }}></i>Remaining Balance
                  </div>
                  <div style={{ fontSize: "16px", fontWeight: "bold", color: "#856404" }}>
                    {formatCurrency(balanceAfter)}
                  </div>
                  {!isPayment && invoice?.amountPaid > 0 && (
                    <div style={{ fontSize: "9px", color: "#856404", opacity: "0.8" }}>
                      Total: {formatCurrency(invoiceTotal)} - Paid: {formatCurrency(invoice?.amountPaid)}
                    </div>
                  )}
                </div>
                {!isPayment && (
                  <button
                    onClick={() => {
                      if (typeof onToggleInvoice === "function") {
                        onToggleInvoice(invoice?.invoiceNumber);
                      }
                    }}
                    style={{
                      background: "none",
                      border: "1px solid #007bff",
                      color: "#007bff",
                      padding: "5px 15px",
                      borderRadius: "5px",
                      fontSize: "14px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    <i className={`bi ${isExpanded ? "bi-chevron-up" : "bi-chevron-down"}`}></i>
                    {isExpanded ? "Hide" : "Show"} Products ({invoice?.products?.length || 0})
                  </button>
                )}
              </div>
            </div>

            {/* Expandable product details */}
            {!isPayment && isExpanded && Array.isArray(invoice?.products) && invoice.products.length > 0 && (
              <div
                style={{
                  marginTop: "15px",
                  border: "1px solid #dee2e6",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    background: "#f8f9fa",
                    padding: "10px 15px",
                    borderBottom: "1px solid #dee2e6",
                    fontWeight: "bold",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "10px",
                  }}
                >
                  <div>
                    <i className="bi bi-box-seam" style={{ marginRight: "8px" }}></i>Products (
                    {invoice.products.length})
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6c757d",
                      display: "flex",
                      gap: "15px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span>
                      Subtotal:{" "}
                      <strong style={{ color: "#20c997" }}>
                        {formatCurrency(invoice.totalBeforeGst)}
                      </strong>
                    </span>
                    <span>
                      Discount:{" "}
                      <strong style={{ color: "#ffc107" }}>
                        -{formatCurrency(invoice.discount || 0)}
                      </strong>
                    </span>
                    <span>
                      Rent:{" "}
                      <strong style={{ color: "#fd7e14" }}>
                        +{formatCurrency(invoice.rent || 0)}
                      </strong>
                    </span>
                    <span>
                      GST ({invoice.gstPercentage || 0}%):{" "}
                      <strong style={{ color: "#dc3545" }}>
                        {formatCurrency(invoice.gstAmount)}
                      </strong>
                    </span>
                    <span>
                      Total: <strong>{formatCurrency(invoiceTotal)}</strong>
                    </span>
                    {invoice.amountPaid > 0 && (
                      <span style={{ color: "#0066cc" }}>
                        Paid: <strong>{formatCurrency(invoice.amountPaid)}</strong>
                      </span>
                    )}
                  </div>
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#e9ecef" }}>
                      <th style={{ padding: "10px 15px", textAlign: "left", borderBottom: "2px solid #dee2e6" }}>
                        #
                      </th>
                      <th style={{ padding: "10px 15px", textAlign: "left", borderBottom: "2px solid #dee2e6" }}>
                        Product Name
                      </th>
                      <th style={{ padding: "10px 15px", textAlign: "left", borderBottom: "2px solid #dee2e6" }}>
                        Category
                      </th>
                      <th style={{ padding: "10px 15px", textAlign: "left", borderBottom: "2px solid #dee2e6" }}>
                        Size
                      </th>
                      <th style={{ padding: "10px 15px", textAlign: "left", borderBottom: "2px solid #dee2e6" }}>
                        Quantity
                      </th>
                      <th style={{ padding: "10px 15px", textAlign: "left", borderBottom: "2px solid #dee2e6" }}>
                        Rate
                      </th>
                      <th style={{ padding: "10px 15px", textAlign: "left", borderBottom: "2px solid #dee2e6" }}>
                        KTAE
                      </th>
                      <th style={{ padding: "10px 15px", textAlign: "left", borderBottom: "2px solid #dee2e6" }}>
                        Gram
                      </th>
                      <th style={{ padding: "10px 15px", textAlign: "left", borderBottom: "2px solid #dee2e6" }}>
                        Total Price
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.products.map((product, pidx) => {
                      const category = product?.category?.toLowerCase?.() || "";
                      const showSizeField = ["roll", "reel", "packet","tota"].includes(category);
                      const showKtae = category === "packet";
                      const showGram = category === "packet";
                      const getSizeOrLength = () => {
                        if (!showSizeField) return "--";
                        if (category === "reel" || category === "tota" ) return product?.length || "--";
                        return product?.size || "--";
                      };
                      return (
                        <tr key={pidx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                          <td style={{ padding: "10px 15px", textAlign: "center" }}>{pidx + 1}</td>
                          <td style={{ padding: "10px 15px", fontWeight: "500" }}>{product?.productName || "—"}</td>
                          <td style={{ padding: "10px 15px", color: "#6c757d" }}>{product?.category || "—"}</td>
                          <td style={{ padding: "10px 15px", textAlign: "center" }}>{getSizeOrLength()}</td>
                          <td style={{ padding: "10px 15px", textAlign: "center", fontWeight: "bold" }}>
                            {product?.quantity ?? 0}
                          </td>
                          <td style={{ padding: "10px 15px", textAlign: "right" }}>
                            {product?.rate ? formatCurrency(product.rate) : "--"}
                          </td>
                          <td style={{ padding: "10px 15px", textAlign: "center" }}>
                            {showKtae ? product?.ktae || "--" : "--"}
                          </td>
                          <td style={{ padding: "10px 15px", textAlign: "center" }}>
                            {showGram ? product?.gram || "--" : "--"}
                          </td>
                          <td
                            style={{
                              padding: "10px 15px",
                              fontWeight: "bold",
                              color: "#28a745",
                              textAlign: "right",
                            }}
                          >
                            {formatCurrency(product?.totalPrice)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      } catch (error) {
        console.error("Error rendering row", error);
        return <div style={style}>Error rendering row</div>;
      }
    },
    [transactions, expandedSet, onToggleInvoice]
  );

  return (
    <div
      style={{
        marginBottom: "30px",
        border: "1px solid #e0e0e0",
        borderRadius: "10px",
        overflow: "hidden",
        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
      }}
    >
      {/* Month header */}
      <div
        style={{
          background: "#f8f9fa",
          padding: "15px 20px",
          borderBottom: "2px solid #dee2e6",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <div
            style={{
              background: "#007bff",
              color: "white",
              padding: "5px 12px",
              borderRadius: "20px",
              fontWeight: "bold",
              fontSize: "14px",
            }}
          >
            {monthData.billingMonth || "Unknown"}
          </div>
          <div style={{ fontSize: "14px", color: "#6c757d" }}>{itemCount} transactions</div>
        </div>
        <div style={{ display: "flex", gap: "15px", fontSize: "14px", flexWrap: "wrap" }}>
          <div>
            <span style={{ color: "#6c757d" }}>Start:</span>{" "}
            <span style={{ fontWeight: "bold" }}>{formatCurrency(monthData.monthStartBalance)}</span>
          </div>
          <div>
            <span style={{ color: "#6c757d" }}>End:</span>{" "}
            <span style={{ fontWeight: "bold" }}>{formatCurrency(monthData.monthEndBalance)}</span>
          </div>
          <div>
            <span style={{ color: "#28a745" }}>Purchases:</span>{" "}
            <span style={{ fontWeight: "bold" }}>{formatCurrency(monthData.monthTotalPurchases)}</span>
          </div>
          <div>
            <span style={{ color: "#dc3545" }}>Returns:</span>{" "}
            <span style={{ fontWeight: "bold" }}>{formatCurrency(monthData.monthTotalReturns)}</span>
          </div>
          <div>
            <span style={{ color: "#17a2b8" }}>Payments:</span>{" "}
            <span style={{ fontWeight: "bold" }}>{formatCurrency(monthData.monthTotalPayments)}</span>
          </div>
          <div>
            <span style={{ color: "#ffc107" }}>Discounts:</span>{" "}
            <span style={{ fontWeight: "bold" }}>{formatCurrency(monthData.monthTotalDiscounts)}</span>
          </div>
          <div>
            <span style={{ color: "#fd7e14" }}>Rents:</span>{" "}
            <span style={{ fontWeight: "bold" }}>{formatCurrency(monthData.monthTotalRents)}</span>
          </div>
          <div>
            <span style={{ color: "#6f42c1" }}>GST:</span>{" "}
            <span style={{ fontWeight: "bold" }}>{formatCurrency(monthData.monthTotalGst)}</span>
          </div>
        </div>
      </div>

      {itemCount > 0 ? (
        <div style={{ maxHeight: "600px", overflowY: "auto" }}>
          {transactions.map((_, idx) => (
            <Row key={idx} index={idx} style={{}} />
          ))}
        </div>
      ) : (
        <div style={{ padding: "30px", textAlign: "center", color: "#6c757d" }}>
          <i className="bi bi-inbox" style={{ fontSize: "48px", marginBottom: "10px", opacity: "0.5" }}></i>
          <p>No transactions found for this month</p>
        </div>
      )}
    </div>
  );
});

// ---------- Main VendorLedger Component ----------
const VendorLedger = () => {
  const [ledgerData, setLedgerData] = useState({ monthLedgers: [] });
  const [loading, setLoading] = useState(false);
  const [vendorlist, setVendorList] = useState([]);
  const [vendorName, setVendorName] = useState("");
  const [startMonth, setStartMonth] = useState("");
  const [endMonth, setEndMonth] = useState("");
  const [expandedInvoices, setExpandedInvoices] = useState(new Set());

  const [showBalanceSheet, setShowBalanceSheet] = useState(false);
  const [balanceSheetMonth, setBalanceSheetMonth] = useState("");
  const [balanceSheetData, setBalanceSheetData] = useState([]);
  const [loadingBalanceSheet, setLoadingBalanceSheet] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    api
      .get("/product/getAll")
      .then((res) => {
        if (res.data.code === 1) setVendorList(res.data.additionalDetail);
      })
      .catch((err) => console.error(err));
  }, []);

  const fetchLedger = () => {
    if (!vendorName) {
      alert("Please select a vendor");
      return;
    }
    setLoading(true);
    setExpandedInvoices(new Set());

    const params = new URLSearchParams();
    if (startMonth) params.append("startMonth", startMonth);
    if (endMonth) params.append("endMonth", endMonth);

    const url = `/api/ledger/vendor/${vendorName}${params.toString() ? "?" + params.toString() : ""}`;

    api
      .get(url)
      .then((res) => {
        const data = res.data || {};
        setLedgerData({
          ...data,
          monthLedgers: Array.isArray(data.monthLedgers) ? data.monthLedgers : [],
        });
      })
      .catch((err) => {
        console.error(err);
        setLedgerData({ monthLedgers: [] });
      })
      .finally(() => setLoading(false));
  };

  const clearFilters = () => {
    setStartMonth("");
    setEndMonth("");
    setVendorName("");
    setLedgerData({ monthLedgers: [] });
    setExpandedInvoices(new Set());
  };

  const toggleInvoiceDetails = useCallback((invoiceNumber) => {
    if (!invoiceNumber) return;
    setExpandedInvoices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(invoiceNumber)) {
        newSet.delete(invoiceNumber);
      } else {
        newSet.add(invoiceNumber);
      }
      return newSet;
    });
  }, []);

  // ---------- UPDATED PRINT (shows separate rows) ----------
  const handlePrint = async () => {
    if (!ledgerData.monthLedgers || ledgerData.monthLedgers.length === 0) {
      alert("No data to print");
      return;
    }
    setIsPrinting(true);

    const formatCurrencyComma = (amount) => {
      if (amount === undefined || amount === null || isNaN(amount)) return "0.00";
      return Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };
    const formatDateOnly = (dateTime) => {
      if (!dateTime) return "--";
      const date = new Date(dateTime);
      return isNaN(date) ? "--" : date.toLocaleDateString();
    };
    const getTransactionImpact = (invoice) => {
      if (!invoice || typeof invoice !== "object") return 0;
      if (invoice.isPayment) return -Number(invoice.amountPaid || 0);
      const beforeGst = Number(invoice.totalBeforeGst || 0);
      const discount = Number(invoice.discount || 0);
      const rent = Number(invoice.rent || 0);
      const gst = Number(invoice.gstAmount || 0);
      const invoiceTotal = beforeGst - discount + rent + gst;
      return invoice.status === "Purchase" ? invoiceTotal : -invoiceTotal;
    };
    const filterZeroPayments = (invoices) => {
      if (!Array.isArray(invoices)) return [];
      return invoices.filter((inv) => {
        if (inv && inv.isPayment) {
          return inv.amountPaid && Number(inv.amountPaid) !== 0;
        }
        return !!inv;
      });
    };

    const htmlParts = [];
    htmlParts.push(`
      <html>
      <head>
        <title>Vendor Ledger - ${vendorName || "All Vendors"}</title>
        <style>
          @page { size: A4 landscape; margin: 15mm 10mm; }
          body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 10px; padding: 0; margin: 0; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }
          .header h1 { margin: 0 0 5px 0; font-size: 20px; }
          .header h3 { margin: 0 0 3px 0; font-size: 16px; color: #666; }
          .header .period { font-size: 12px; color: #888; }
          .header .gen-date { font-size: 10px; color: #999; margin-top: 5px; }
          .month-header { background-color: #333; color: #fff; padding: 6px 10px; font-weight: bold; font-size: 11px; margin-top: 20px; margin-bottom: 8px; display: flex; justify-content: space-between; }
          .month-header span { margin-right: 15px; }
          table { width: 100%; border-collapse: collapse; font-size: 9px; margin-bottom: 20px; }
          table th { background-color: #e9ecef; border: 1px solid #000; padding: 4px 6px; text-align: left; font-weight: bold; }
          table td { border: 1px solid #000; padding: 3px 6px; vertical-align: middle; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .bold { font-weight: bold; }
          .opening-row { background-color: #f8f9fa; font-style: italic; }
          .payment-row { background-color: #d4edda; }
          .return-row { background-color: #fff3cd; }
          .debit { color: #28a745; }
          .credit { color: #dc3545; }
          .balance { font-weight: bold; }
          .page-break { page-break-before: always; }
          .footer { text-align: center; margin-top: 20px; padding-top: 10px; border-top: 1px solid #ddd; font-size: 9px; color: #666; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>VENDOR LEDGER</h1>
          <h3>${vendorName || "All Vendors"}</h3>
          <div class="period">Period: ${startMonth || "Start"} to ${endMonth || "End"}</div>
          <div class="gen-date">Generated on: ${new Date().toLocaleString()}</div>
        </div>
    `);

    for (let mi = 0; mi < ledgerData.monthLedgers.length; mi++) {
      const monthData = ledgerData.monthLedgers[mi];
      if (!monthData || typeof monthData !== "object") continue;
      const filtered = filterZeroPayments(monthData.invoices);
      const transactions = [...filtered].sort((a, b) => {
        const dateA = getTransactionDate(a);
        const dateB = getTransactionDate(b);
        if (dateA - dateB !== 0) return dateA - dateB;
        if (a.isPayment && !b.isPayment) return 1;
        if (!a.isPayment && b.isPayment) return -1;
        return (parseInt(a.invoiceNumber) || 0) - (parseInt(b.invoiceNumber) || 0);
      });
      let runningBalance = Number(monthData.monthStartBalance || 0);

      htmlParts.push(`
        <div class="month-header">
          <span>Month: ${monthData.billingMonth || "Unknown"}</span>
          <span>Start Balance: ${formatCurrencyComma(monthData.monthStartBalance)}</span>
          <span>End Balance: ${formatCurrencyComma(monthData.monthEndBalance)}</span>
          <span>Purchases: ${formatCurrencyComma(monthData.monthTotalPurchases)}</span>
          <span>Returns: ${formatCurrencyComma(monthData.monthTotalReturns)}</span>
          <span>Payments: ${formatCurrencyComma(monthData.monthTotalPayments)}</span>
        </div>
        <table>
          <thead>
            <tr>
              <th style="width:10%;">Date</th>
              <th style="width:8%;">Voucher #</th>
              <th style="width:8%;">Size</th>
              <th style="width:32%;">Description</th>
              <th style="width:6%; text-align:right;">Qty</th>
              <th style="width:8%; text-align:right;">Rate</th>
              <th style="width:10%; text-align:right;">Debit</th>
              <th style="width:10%; text-align:right;">Credit</th>
              <th style="width:12%; text-align:right;">Balance</th>
            </tr>
          </thead>
          <tbody>
      `);

      // Opening Balance
      htmlParts.push(`
        <tr class="opening-row">
          <td>${monthData.billingMonth || "??"}-01</td>
          <td>---</td>
          <td>---</td>
          <td>Opening Balance</td>
          <td class="text-right">---</td>
          <td class="text-right">---</td>
          <td class="text-right">${formatCurrencyComma(monthData.monthStartBalance > 0 ? monthData.monthStartBalance : 0)}</td>
          <td class="text-right">${formatCurrencyComma(monthData.monthStartBalance < 0 ? Math.abs(monthData.monthStartBalance) : 0)}</td>
          <td class="text-right balance">${formatCurrencyComma(monthData.monthStartBalance)}</td>
        </tr>
      `);

      // Process each transaction
      for (let ti = 0; ti < transactions.length; ti++) {
        const invoice = transactions[ti];
        if (!invoice) continue;
        const isPayment = invoice.isPayment === true;

        // ---- Payment ----
        if (isPayment) {
          const impact = getTransactionImpact(invoice);
          runningBalance += impact;
          let debit = 0, credit = 0;
          if (impact > 0) debit = impact;
          else if (impact < 0) credit = Math.abs(impact);
          htmlParts.push(`
            <tr class="payment-row">
              <td>${formatDateOnly(invoice.paymentTime)}</td>
              <td>PAYMENT</td>
              <td class="text-center">---</td>
              <td>PAYMENT MADE</td>
              <td class="text-right">---</td>
              <td class="text-right">---</td>
              <td class="text-right ${debit > 0 ? 'debit' : ''}">${debit > 0 ? formatCurrencyComma(debit) : '---'}</td>
              <td class="text-right ${credit > 0 ? 'credit' : ''}">${credit > 0 ? formatCurrencyComma(credit) : '---'}</td>
              <td class="text-right balance">${formatCurrencyComma(runningBalance)}</td>
            </tr>
          `);
          continue;
        }

        // ---- Invoice ----
        const products = invoice.products || [];
        const isPurchase = invoice.status === "Purchase";
        const invoiceTotal = (invoice.totalBeforeGst || 0) - (invoice.discount || 0) + (invoice.rent || 0) + (invoice.gstAmount || 0);
        const rowClass = invoice.status === "Return" ? "return-row" : "";

        // 1. Products
        products.forEach((product, pIdx) => {
          const productTotal = product.totalPrice || (product.quantity * product.rate);
          const debit = isPurchase ? productTotal : 0;
          const credit = isPurchase ? 0 : productTotal;
          const size = getSizeDisplay(product);
          const desc = `${product.productName || ''} / ${getDetail(product)}`;
          htmlParts.push(`
            <tr class="${rowClass}">
              <td>${formatDateOnly(invoice.purchaseRecordTime || invoice.returnRecordTime)}</td>
              <td>${pIdx === 0 ? `#${invoice.invoiceNumber}` : ''}</td>
              <td class="text-center">${size}</td>
              <td>${desc}</td>
              <td class="text-right">${product.quantity || 0}</td>
              <td class="text-right">${formatCurrencyComma(product.rate || product.price || 0)}</td>
              <td class="text-right ${debit > 0 ? 'debit' : ''}">${debit > 0 ? formatCurrencyComma(debit) : '---'}</td>
              <td class="text-right ${credit > 0 ? 'credit' : ''}">${credit > 0 ? formatCurrencyComma(credit) : '---'}</td>
              <td class="text-right"></td>
            </tr>
          `);
        });

        // 2. Discount
        if (isPurchase && invoice.discount > 0) {
          htmlParts.push(`
            <tr>
              <td>${formatDateOnly(invoice.purchaseRecordTime)}</td>
              <td></td>
              <td>---</td>
              <td>Discount</td>
              <td>---</td>
              <td>---</td>
              <td>---</td>
              <td class="text-right credit">${formatCurrencyComma(invoice.discount)}</td>
              <td></td>
            </tr>
          `);
        }

        // 3. Rent
        if (isPurchase && invoice.rent > 0) {
          htmlParts.push(`
            <tr>
              <td>${formatDateOnly(invoice.purchaseRecordTime)}</td>
              <td></td>
              <td>---</td>
              <td>Rent</td>
              <td>---</td>
              <td>---</td>
              <td class="text-right debit">${formatCurrencyComma(invoice.rent)}</td>
              <td>---</td>
              <td></td>
            </tr>
          `);
        }

        // 4. GST
        if (isPurchase && invoice.gstAmount > 0) {
          htmlParts.push(`
            <tr>
              <td>${formatDateOnly(invoice.purchaseRecordTime)}</td>
              <td></td>
              <td>---</td>
              <td>GST (${invoice.gstPercentage || 0}%)</td>
              <td>---</td>
              <td>---</td>
              <td class="text-right debit">${formatCurrencyComma(invoice.gstAmount)}</td>
              <td>---</td>
              <td></td>
            </tr>
          `);
        }

        // 5. Invoice Total – update balance
        const impact = isPurchase ? invoiceTotal : -invoiceTotal;
        runningBalance += impact;
        htmlParts.push(`
          <tr style="font-weight:bold; background:#e9ecef; border-bottom:2px solid #000;">
            <td>${formatDateOnly(invoice.purchaseRecordTime || invoice.returnRecordTime)}</td>
            <td></td>
            <td>---</td>
            <td>Invoice Total</td>
            <td>---</td>
            <td>---</td>
            <td class="text-right ${isPurchase ? 'debit' : ''}">${isPurchase ? formatCurrencyComma(invoiceTotal) : '---'}</td>
            <td class="text-right ${!isPurchase ? 'credit' : ''}">${!isPurchase ? formatCurrencyComma(invoiceTotal) : '---'}</td>
            <td class="text-right balance">${formatCurrencyComma(runningBalance)}</td>
          </tr>
        `);
      }

      htmlParts.push(`</tbody></table>`);
      if (mi < ledgerData.monthLedgers.length - 1) {
        htmlParts.push(`<div class="page-break"></div>`);
      }
      if (mi % 5 === 0) await new Promise((resolve) => setTimeout(resolve, 0));
    }

    if (ledgerData.totalBalance !== undefined) {
      htmlParts.push(`
        <div style="background: #f8f9fa; padding: 10px; border: 1px solid #ddd; margin-top: 20px; font-size: 12px; display: flex; justify-content: space-between;">
          <span><strong>Total Outstanding Balance:</strong></span>
          <span><strong>${formatCurrencyComma(ledgerData.totalBalance)}</strong></span>
        </div>
      `);
    }

    htmlParts.push(`
      <div class="footer">--- End of Report ---</div>
    </body></html>
    `);

    const finalHTML = htmlParts.join("");
    const printWindow = window.open("", "_blank", "width=1024,height=800,scrollbars=yes");
    if (printWindow) {
      printWindow.document.write(finalHTML);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        setIsPrinting(false);
      }, 500);
    } else {
      alert("Please allow pop-ups for this site to print the ledger.");
      setIsPrinting(false);
    }
  };

  // ---------- Balance Sheet handlers ----------
  const fetchBalanceSheet = () => {
    setLoadingBalanceSheet(true);
    const params = new URLSearchParams();
    if (balanceSheetMonth) params.append("month", balanceSheetMonth);

    api
      .get(`/product/vendor-balance-sheet?${params.toString()}`)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : [];
        const sorted = data.sort((a, b) => {
          const nameA = (a.vendorName || a.customerName || "").toLowerCase();
          const nameB = (b.vendorName || b.customerName || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });
        const filteredData = sorted.filter(item => Math.abs(Number(item.balance)) > 0.000001);
        setBalanceSheetData(filteredData);
      })
      .catch((err) => {
        console.error(err);
        alert("Failed to fetch balance sheet");
      })
      .finally(() => setLoadingBalanceSheet(false));
  };

  const closeBalanceSheet = () => {
    setShowBalanceSheet(false);
    setBalanceSheetData([]);
  };

  const printBalanceSheet = () => {
    if (!balanceSheetData || balanceSheetData.length === 0) {
      alert("No data to print");
      return;
    }
    const formatCurrencyComma = (amount) => {
      if (amount === undefined || amount === null || isNaN(amount)) return "0.00";
      return Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };
    const total = balanceSheetData.reduce((sum, item) => sum + Number(item.balance || 0), 0);
    const half = Math.ceil(balanceSheetData.length / 2);
    const leftData = balanceSheetData.slice(0, half);
    const rightData = balanceSheetData.slice(half);

    const buildColumnRows = (data, startIndex) => {
      return data.map((item, idx) => {
        const index = startIndex + idx;
        const name = item.vendorName || item.customerName || "—";
        const balance = Number(item.balance) || 0;
        const color = balance < 0 ? "#dc3545" : "#198754";
        const lastSale = formatDateOnly(item.lastSaleDate);
        const lastPayment = formatDateOnly(item.lastPaymentDate);
        return `
          <tr style="border-bottom: 1px solid #f1f3f5; background: ${index % 2 === 0 ? "#fff" : "#f8f9fa"};">
            <td style="padding: 6px 10px; text-align: center; color: #6c757d; width: 40px;">${index + 1}</td>
            <td style="padding: 6px 10px; font-weight: 500; color: #212529;">${name}</td>
            <td style="padding: 6px 10px; color: #495057; text-align: center;">${lastSale}</td>
            <td style="padding: 6px 10px; color: #495057; text-align: center;">${lastPayment}</td>
            <td style="padding: 6px 10px; text-align: right; font-weight: 600; color: ${color};">${formatCurrencyComma(balance)}</td>
          </tr>
        `;
      }).join("");
    };

    const leftRows = buildColumnRows(leftData, 0);
    const rightRows = buildColumnRows(rightData, half);

    const printContent = `
      <div style="padding: 15px; font-family: 'Segoe UI', Roboto, Arial, sans-serif; max-width: 1100px; margin: 0 auto;">
        <div style="text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 12px;">
          <h1 style="margin: 0 0 5px 0; font-size: 24px; color: #212529;">Balance Sheet</h1>
          <h3 style="margin: 5px 0; font-size: 16px; color: #495057; font-weight: 400;">
            ${balanceSheetMonth ? `Month: ${balanceSheetMonth}` : "Latest Balances per Vendor"}
          </h3>
          <div style="font-size: 12px; color: #6c757d; margin-top: 3px;">
            Generated on: ${new Date().toLocaleString()}
          </div>
        </div>
        <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
          <tr>
            <td style="width: 50%; vertical-align: top; padding-right: 10px;">
              <table style="width: 100%; border-collapse: collapse; box-shadow: 0 1px 3px rgba(0,0,0,0.05);">
                <thead>
                  <tr style="background: #f1f3f5; border-bottom: 2px solid #dee2e6;">
                    <th style="padding: 8px 10px; text-align: left; font-weight: 600; color: #495057; width: 40px;">#</th>
                    <th style="padding: 8px 10px; text-align: left; font-weight: 600; color: #495057;">Vendor Name</th>
                    <th style="padding: 8px 10px; text-align: right; font-weight: 600; color: #495057;">Balance</th>
                  </tr>
                </thead>
                <tbody>${leftRows}</tbody>
              </table>
            </td>
            <td style="width: 50%; vertical-align: top; padding-left: 10px;">
              <table style="width: 100%; border-collapse: collapse; box-shadow: 0 1px 3px rgba(0,0,0,0.05);">
                <thead>
                  <tr style="background: #f1f3f5; border-bottom: 2px solid #dee2e6;">
                    <th style="padding: 8px 10px; text-align: left; font-weight: 600; color: #495057; width: 40px;">#</th>
                    <th style="padding: 8px 10px; text-align: left; font-weight: 600; color: #495057;">Vendor Name</th>
                    <th style="padding: 8px 10px; text-align: right; font-weight: 600; color: #495057;">Balance</th>
                  </tr>
                </thead>
                <tbody>${rightRows}</tbody>
              </table>
            </td>
          </tr>
        </table>
        <div style="margin-top: 15px; padding: 10px 15px; background: #e9ecef; border-radius: 4px; display: flex; justify-content: space-between; font-size: 14px; font-weight: 600;">
          <span>Total Outstanding</span>
          <span style="color: #0d6efd;">${formatCurrencyComma(total)}</span>
        </div>
        <div style="margin-top: 15px; padding-top: 10px; border-top: 1px solid #e9ecef; display: flex; justify-content: space-between; font-size: 11px; color: #6c757d;">
          <span>Total records: ${balanceSheetData.length}</span>
          <span>Generated on: ${new Date().toLocaleString()}</span>
        </div>
      </div>
    `;

    const win = window.open("", "_blank", "width=900,height=700");
    if (win) {
      win.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Vendor Balance Sheet</title>
          <style>
            @media print { @page { margin: 12mm 10mm; } body { margin: 0; padding: 0; } }
            body { font-family: 'Segoe UI', Roboto, Arial, sans-serif; padding: 15px; background: #fff; }
            table { page-break-inside: avoid; }
            td { vertical-align: top; }
          </style>
        </head>
        <body>${printContent}</body>
        </html>
      `);
      win.document.close();
      setTimeout(() => { win.focus(); win.print(); }, 500);
    }
  };

  // ---------- UPDATED EXCEL EXPORT (shows separate rows) ----------
  const exportLedgerToExcel = async () => {
    if (!ledgerData.monthLedgers || ledgerData.monthLedgers.length === 0) {
      alert("No ledger data to export.");
      return;
    }

    setIsExporting(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Vendor Ledger');

      worksheet.columns = [
        { width: 14 }, { width: 12 }, { width: 10 }, { width: 35 },
        { width: 10 }, { width: 12 }, { width: 14 }, { width: 14 }, { width: 16 }
      ];

      let currentRow = 1;

      // Local helpers
      const formatDateOnly = (dateTime) => {
        if (!dateTime) return "--";
        const date = new Date(dateTime);
        return isNaN(date) ? "--" : date.toLocaleDateString();
      };

      const getTransactionImpact = (invoice) => {
        if (!invoice) return 0;
        if (invoice.isPayment) return -Number(invoice.amountPaid || 0);
        const beforeGst = Number(invoice.totalBeforeGst || 0);
        const discount = Number(invoice.discount || 0);
        const rent = Number(invoice.rent || 0);
        const gst = Number(invoice.gstAmount || 0);
        const invoiceTotal = beforeGst - discount + rent + gst;
        return invoice.status === "Purchase" ? invoiceTotal : -invoiceTotal;
      };

      const sortTransactions = (transactions) => {
        return [...transactions].sort((a, b) => {
          const dateA = getTransactionDate(a);
          const dateB = getTransactionDate(b);
          if (dateA - dateB !== 0) return dateA - dateB;
          if (a.isPayment && !b.isPayment) return 1;
          if (!a.isPayment && b.isPayment) return -1;
          return (parseInt(a.invoiceNumber) || 0) - (parseInt(b.invoiceNumber) || 0);
        });
      };

      // Title
      worksheet.mergeCells(`A${currentRow}:I${currentRow}`);
      const titleRow = worksheet.getRow(currentRow);
      titleRow.height = 30;
      titleRow.getCell(1).value = 'VENDOR LEDGER';
      titleRow.eachCell((cell) => {
        cell.font = { bold: true, size: 16, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2F75B5' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      });
      currentRow++;

      worksheet.mergeCells(`A${currentRow}:I${currentRow}`);
      const subRow = worksheet.getRow(currentRow);
      subRow.height = 22;
      subRow.getCell(1).value = `Vendor: ${vendorName || "All Vendors"}`;
      subRow.eachCell((cell) => {
        cell.font = { bold: true, size: 12, color: { argb: 'FF333333' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      });
      currentRow++;

      worksheet.mergeCells(`A${currentRow}:I${currentRow}`);
      const periodRow = worksheet.getRow(currentRow);
      periodRow.height = 20;
      periodRow.getCell(1).value = `Period: ${startMonth || "Start"} to ${endMonth || "End"} | Generated: ${new Date().toLocaleString()}`;
      periodRow.eachCell((cell) => {
        cell.font = { size: 10, color: { argb: 'FF666666' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      });
      currentRow += 2;

      // Months
      ledgerData.monthLedgers.forEach((monthData) => {
        const filtered = filterZeroPayments(monthData.invoices || []);
        const transactions = sortTransactions(filtered);
        let runningBalance = Number(monthData.monthStartBalance || 0);

        // Month Header
        worksheet.mergeCells(`A${currentRow}:I${currentRow}`);
        const monthHeader = worksheet.getRow(currentRow);
        monthHeader.height = 24;
        monthHeader.getCell(1).value = `Month: ${monthData.billingMonth}`;
        monthHeader.eachCell((cell) => {
          cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E7E34' } };
          cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
        });
        currentRow++;

        // Column Headers
        const headers = ['Date', 'Voucher #', 'Size', 'Description', 'Qty', 'Rate', 'Debit', 'Credit', 'Balance'];
        const headerRow = worksheet.addRow(headers);
        headerRow.height = 20;
        headerRow.eachCell((cell) => {
          cell.font = { bold: true, size: 10 };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
        currentRow++;

        // Opening Balance
        const openRow = worksheet.addRow([
          `${monthData.billingMonth}-01`, '---', '---', 'Opening Balance', '---', '---',
          monthData.monthStartBalance > 0 ? monthData.monthStartBalance : 0,
          monthData.monthStartBalance < 0 ? Math.abs(monthData.monthStartBalance) : 0,
          monthData.monthStartBalance
        ]);
        openRow.eachCell((cell, col) => {
          if (col >= 7) cell.numFmt = '#,##0.00';
          cell.alignment = { horizontal: col === 4 ? 'left' : 'center', vertical: 'middle' };
          cell.font = { italic: true };
          cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };
        });
        currentRow++;

        // Transactions
        transactions.forEach((invoice) => {
          const isPayment = invoice.isPayment === true;

          // Payment
          if (isPayment) {
            const impact = getTransactionImpact(invoice);
            runningBalance += impact;
            let debit = 0, credit = 0;
            if (impact > 0) debit = impact;
            else if (impact < 0) credit = Math.abs(impact);
            const row = worksheet.addRow([
              formatDateOnly(invoice.paymentTime),
              'PAYMENT',
              '---',
              'PAYMENT MADE',
              '---',
              '---',
              debit,
              credit,
              runningBalance
            ]);
            row.eachCell((cell, col) => {
              if (col >= 7) cell.numFmt = '#,##0.00';
              cell.alignment = { horizontal: col === 4 ? 'left' : 'center', vertical: 'middle' };
              if (col === 9) cell.font = { bold: true };
              cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
            currentRow++;
            return;
          }

          // Invoice
          const products = invoice.products || [];
          const isPurchase = invoice.status === "Purchase";
          const invoiceTotal = (invoice.totalBeforeGst || 0) - (invoice.discount || 0) + (invoice.rent || 0) + (invoice.gstAmount || 0);

          // 1. Products
          products.forEach((product, pIdx) => {
            const productTotal = product.totalPrice || (product.quantity * product.rate);
            const debit = isPurchase ? productTotal : 0;
            const credit = isPurchase ? 0 : productTotal;
            const size = getSizeDisplay(product);
            const desc = `${product.productName || ''} / ${getDetail(product)}`;
            const row = worksheet.addRow([
              formatDateOnly(invoice.purchaseRecordTime || invoice.returnRecordTime),
              pIdx === 0 ? `#${invoice.invoiceNumber}` : '',
              size,
              desc,
              product.quantity || 0,
              product.rate || product.price || 0,
              debit,
              credit,
              '' // balance empty
            ]);
            row.eachCell((cell, col) => {
              if (col >= 5 && col <= 9) { if (col === 5) cell.numFmt = '#,##0'; else cell.numFmt = '#,##0.00'; }
              cell.alignment = { horizontal: col === 4 ? 'left' : 'center', vertical: 'middle' };
              cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
            currentRow++;
          });

          // 2. Discount
          if (isPurchase && invoice.discount > 0) {
            const row = worksheet.addRow([
              formatDateOnly(invoice.purchaseRecordTime),
              '', '', 'Discount', '---', '---', 0, invoice.discount, ''
            ]);
            row.eachCell((cell, col) => {
              if (col >= 7) cell.numFmt = '#,##0.00';
              cell.alignment = { horizontal: col === 4 ? 'left' : 'center', vertical: 'middle' };
              cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
            currentRow++;
          }

          // 3. Rent
          if (isPurchase && invoice.rent > 0) {
            const row = worksheet.addRow([
              formatDateOnly(invoice.purchaseRecordTime),
              '', '', 'Rent', '---', '---', invoice.rent, 0, ''
            ]);
            row.eachCell((cell, col) => {
              if (col >= 7) cell.numFmt = '#,##0.00';
              cell.alignment = { horizontal: col === 4 ? 'left' : 'center', vertical: 'middle' };
              cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
            currentRow++;
          }

          // 4. GST
          if (isPurchase && invoice.gstAmount > 0) {
            const row = worksheet.addRow([
              formatDateOnly(invoice.purchaseRecordTime),
              '', '', `GST (${invoice.gstPercentage || 0}%)`, '---', '---', invoice.gstAmount, 0, ''
            ]);
            row.eachCell((cell, col) => {
              if (col >= 7) cell.numFmt = '#,##0.00';
              cell.alignment = { horizontal: col === 4 ? 'left' : 'center', vertical: 'middle' };
              cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
            currentRow++;
          }

          // 5. Invoice Total – update balance
          // 5. Invoice Total – update balance
const impact = isPurchase ? invoiceTotal : -invoiceTotal;
runningBalance += impact;
const row = worksheet.addRow([
  formatDateOnly(invoice.purchaseRecordTime || invoice.returnRecordTime),
  '', '', 'Invoice Total', '---', '---',
  isPurchase ? invoiceTotal : 0,
  isPurchase ? 0 : invoiceTotal,
  runningBalance
]);
row.eachCell((cell, col) => {
  // Apply currency format for numeric columns (7,8,9)
  if (col >= 7) cell.numFmt = '#,##0.00';
  // Center align except description (col 4)
  cell.alignment = { horizontal: col === 4 ? 'left' : 'center', vertical: 'middle' };
  // Bold all cells in this row
  cell.font = { bold: true };
  // Add background fill (light blue)
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFD4E6F1' }  // Light blue
  };
  // Border with a thicker top
  cell.border = {
    top: { style: 'medium' },   // thicker top
    left: { style: 'thin' },
    bottom: { style: 'thin' },
    right: { style: 'thin' }
  };
});
currentRow++;
        });

        currentRow++; // spacing
      });

      // Grand Total
      if (ledgerData.totalBalance !== undefined) {
        worksheet.mergeCells(`A${currentRow}:G${currentRow}`);
        const totalRow = worksheet.getRow(currentRow);
        totalRow.height = 25;
        totalRow.getCell(1).value = 'Total Outstanding Balance:';
        totalRow.getCell(9).value = ledgerData.totalBalance;
        totalRow.eachCell((cell, col) => {
          cell.font = { bold: true, size: 12 };
          cell.alignment = { horizontal: col === 9 ? 'center' : 'right', vertical: 'middle' };
          if (col === 9) {
            cell.font = { bold: true, size: 12, color: { argb: 'FF0D6EFD' } };
            cell.numFmt = '#,##0.00';
          }
          cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'medium' }, right: { style: 'thin' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };
        });
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const fileName = `vendor_ledger_${vendorName || 'all'}_${new Date().toISOString().slice(0,10)}.xlsx`;
      saveAs(blob, fileName);

    } catch (error) {
      console.error("Export error:", error);
      alert("Failed to export Vendor Ledger.");
    } finally {
      setIsExporting(false);
    }
  };

  const exportBalanceSheetToExcel = async () => {
    if (!balanceSheetData || balanceSheetData.length === 0) {
      alert("No balance sheet data to export.");
      return;
    }

    setIsExporting(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Vendor Balance Sheet');

      worksheet.columns = [
        { width: 8 },   // #
        { width: 30 },  // Vendor Name
        { width: 15 },  // Month
        { width: 18 },  // Last Sale Date
        { width: 18 },  // Last Payment Date
        { width: 18 },  // Balance
      ];

      let currentRow = 1;

      // Title
      worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
      const titleRow = worksheet.getRow(currentRow);
      titleRow.height = 30;
      titleRow.getCell(1).value = 'VENDOR BALANCE SHEET';
      titleRow.eachCell((cell) => {
        cell.font = { bold: true, size: 16, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E7E34' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      });
      currentRow++;

      worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
      const subRow = worksheet.getRow(currentRow);
      subRow.height = 20;
      subRow.getCell(1).value = `Month: ${balanceSheetMonth || "Latest Balances"} | Generated: ${new Date().toLocaleString()}`;
      subRow.eachCell((cell) => {
        cell.font = { size: 10, color: { argb: 'FF666666' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      });
      currentRow += 2;

      // Headers
      const headers = ['#', 'Vendor Name', 'Month', 'Last Sale Date', 'Last Payment Date', 'Balance'];
      const headerRow = worksheet.addRow(headers);
      headerRow.height = 20;
      headerRow.eachCell((cell, col) => {
        cell.font = { bold: true, size: 11, color: { argb: 'FF000000' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
        cell.alignment = { horizontal: col === 2 ? 'left' : 'center', vertical: 'middle' };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'medium' }, right: { style: 'thin' } };
      });
      currentRow++;

      let grandTotal = 0;
      balanceSheetData.forEach((item, index) => {
        const balance = Number(item.balance) || 0;
        grandTotal += balance;

        const row = worksheet.addRow([
          index + 1,
          item.vendorName || item.customerName || '—',
          item.billingMonth || 'N/A',
          item.lastSaleDate ? formatDateOnly(item.lastSaleDate) : '--',
          item.lastPaymentDate ? formatDateOnly(item.lastPaymentDate) : '--',
          balance
        ]);

        row.eachCell((cell, col) => {
          cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
          if (col === 1) cell.alignment = { horizontal: 'center', vertical: 'middle' };
          else if (col === 2) { cell.alignment = { horizontal: 'left', vertical: 'middle' }; cell.font = { bold: true }; }
          else if (col === 3) cell.alignment = { horizontal: 'center', vertical: 'middle' };
          else if (col === 6) {
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
            cell.numFmt = '#,##0.00';
            if (balance < 0) cell.font = { color: { argb: 'FFDC3545' } };
          }
        });
        currentRow++;
      });

      // Grand Total
      worksheet.mergeCells(`A${currentRow}:E${currentRow}`);
      const totalRow = worksheet.getRow(currentRow);
      totalRow.height = 22;
      totalRow.getCell(1).value = 'Total Outstanding Balance';
      totalRow.getCell(6).value = grandTotal;
      totalRow.eachCell((cell, col) => {
        cell.font = { bold: true, size: 12 };
        cell.alignment = { horizontal: col === 6 ? 'right' : 'right', vertical: 'middle' };
        if (col === 6) {
          cell.font = { bold: true, size: 12, color: { argb: 'FF0D6EFD' } };
          cell.numFmt = '#,##0.00';
        }
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'medium' }, right: { style: 'thin' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const fileName = `vendor_balance_sheet_${balanceSheetMonth || 'all'}_${new Date().toISOString().slice(0,10)}.xlsx`;
      saveAs(blob, fileName);

    } catch (error) {
      console.error("Export error:", error);
      alert("Failed to export Vendor Balance Sheet.");
    } finally {
      setIsExporting(false);
    }
  };

  // ---------- Render ----------
  return (
    <>
      <div className="purchase-container">
        <h2 className="page-title">Vendor Ledger</h2>

        <div
          className="filter-section"
          style={{
            background: "#f8f9fa",
            padding: "15px",
            borderRadius: "8px",
            marginBottom: "20px",
            border: "1px solid #dee2e6",
          }}
        >
          <div
            className="filter-row"
            style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginBottom: "15px" }}
          >
            <div className="filter-group">
              <label className="filter-label">Vendor Name *</label>
              <div style={{ display: "flex", gap: "10px" }}>
                <input
                  type="text"
                  list="vendorNames"
                  value={vendorName}
                  onChange={(e) => setVendorName(e.target.value)}
                  placeholder="Select vendor"
                  className="form-control"
                  style={{ width: "250px" }}
                />
                <datalist id="vendorNames">
                  {vendorlist.map((v, i) => (
                    <option key={i} value={v.vendorName} />
                  ))}
                </datalist>
              </div>
            </div>
            <div className="filter-group">
              <label className="filter-label">Start Month</label>
              <input
                type="month"
                value={startMonth}
                onChange={(e) => setStartMonth(e.target.value)}
                className="form-control"
                style={{ width: "180px" }}
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">End Month</label>
              <input
                type="month"
                value={endMonth}
                onChange={(e) => setEndMonth(e.target.value)}
                className="form-control"
                style={{ width: "180px" }}
              />
            </div>
          </div>

          <div className="filter-buttons" style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <button
              className="btn btn-primary"
              onClick={fetchLedger}
              disabled={!vendorName || loading}
            >
              <i className="bi bi-search" style={{ marginRight: "5px" }}></i>
              {loading ? "Loading..." : "Fetch Ledger"}
            </button>
            <button className="btn btn-secondary" onClick={clearFilters}>
              <i className="bi bi-x-circle" style={{ marginRight: "5px" }}></i>Clear All
            </button>
            <button className="btn btn-info" onClick={() => setShowBalanceSheet(true)}>
              <i className="bi bi-file-spreadsheet" style={{ marginRight: "5px" }}></i>Balance Sheet
            </button>
            <button
              className="btn btn-primary"
              onClick={exportLedgerToExcel}
              disabled={isExporting || !ledgerData.monthLedgers || ledgerData.monthLedgers.length === 0}
              style={{ backgroundColor: "#217346", borderColor: "#217346" }}
            >
              <i className="bi bi-file-earmark-excel" style={{ marginRight: "5px" }}></i>
              {isExporting ? "Exporting..." : "Export to Excel"}
            </button>
            <button
              className="btn btn-success"
              onClick={handlePrint}
              disabled={!ledgerData.monthLedgers || ledgerData.monthLedgers.length === 0 || isPrinting}
              style={{ marginLeft: "auto" }}
            >
              <i className="bi bi-printer" style={{ marginRight: "5px" }}></i>
              {isPrinting ? "Printing..." : "Print Ledger"}
            </button>
          </div>

          <div className="filter-info" style={{ marginTop: "10px", fontSize: "12px", color: "#6c757d" }}>
            <i className="bi bi-info-circle" style={{ marginRight: "5px" }}></i>
            Leave month fields empty to get all records. Format: YYYY-MM (e.g., 2024-01)
          </div>
        </div>

        {/* Balance Sheet Modal */}
        {showBalanceSheet && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
              padding: "20px",
            }}
            onClick={closeBalanceSheet}
          >
            <div
              style={{
                background: "white",
                borderRadius: "12px",
                padding: "30px 35px",
                maxWidth: "900px",
                width: "100%",
                maxHeight: "85vh",
                overflowY: "auto",
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                  borderBottom: "2px solid #e9ecef",
                  paddingBottom: "15px",
                }}
              >
                <div>
                  <h2 style={{ margin: 0, fontSize: "22px", fontWeight: "600", color: "#212529" }}>
                    <i className="bi bi-file-spreadsheet" style={{ marginRight: "12px", color: "#0d6efd" }}></i>
                    Balance Sheet
                  </h2>
                  <p style={{ margin: "5px 0 0 0", fontSize: "14px", color: "#6c757d" }}>
                    {balanceSheetMonth ? `Month: ${balanceSheetMonth}` : "Latest balances per vendor"}
                  </p>
                </div>
                <button className="btn-close" onClick={closeBalanceSheet} style={{ fontSize: "20px" }}></button>
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "15px",
                  marginBottom: "25px",
                  background: "#f8f9fa",
                  padding: "15px 20px",
                  borderRadius: "8px",
                  alignItems: "flex-end",
                }}
              >
                <div style={{ flex: "1", minWidth: "200px" }}>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "500", color: "#495057", marginBottom: "4px" }}>
                    Select Month (optional)
                  </label>
                  <input
                    type="month"
                    value={balanceSheetMonth}
                    onChange={(e) => setBalanceSheetMonth(e.target.value)}
                    className="form-control"
                    style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #ced4da" }}
                  />
                  <small style={{ color: "#6c757d" }}>Leave empty for latest balance per vendor</small>
                </div>
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <button
                    className="btn btn-primary"
                    onClick={fetchBalanceSheet}
                    disabled={loadingBalanceSheet}
                    style={{
                      padding: "8px 20px",
                      borderRadius: "6px",
                      fontWeight: "500",
                      background: "#0d6efd",
                      border: "none",
                      color: "white",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    {loadingBalanceSheet ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status"></span> Loading...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-search"></i> Fetch
                      </>
                    )}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={exportBalanceSheetToExcel}
                    disabled={isExporting || !balanceSheetData || balanceSheetData.length === 0}
                    style={{ padding: "8px 20px", borderRadius: "6px", fontWeight: "500", background: "#217346", border: "none", color: "white", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}
                  >
                    <i className="bi bi-file-earmark-excel"></i>
                    {isExporting ? " Exporting..." : " Export to Excel"}
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={printBalanceSheet}
                    disabled={!balanceSheetData || balanceSheetData.length === 0}
                    style={{
                      padding: "8px 20px",
                      borderRadius: "6px",
                      fontWeight: "500",
                      background: "#198754",
                      border: "none",
                      color: "white",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <i className="bi bi-printer"></i> Print
                  </button>
                </div>
              </div>
              {balanceSheetData.length > 0 ? (
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "14px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    }}
                  >
                    <thead>
                      <tr style={{ background: "#f1f3f5", borderBottom: "2px solid #dee2e6" }}>
                        <th style={{ padding: "12px 15px", textAlign: "left", fontWeight: "600", color: "#495057", width: "50px" }}>#</th>
                        <th style={{ padding: "12px 15px", textAlign: "left", fontWeight: "600", color: "#495057" }}>Vendor Name</th>
                        <th style={{ padding: "12px 15px", textAlign: "left", fontWeight: "600", color: "#495057" }}>Month</th>
                        <th style={{ padding: "12px 15px", textAlign: "left", fontWeight: "600", color: "#495057" }}>Last Sale Date</th>
                        <th style={{ padding: "12px 15px", textAlign: "left", fontWeight: "600", color: "#495057" }}>Last Payment Date</th>
                        <th style={{ padding: "12px 15px", textAlign: "right", fontWeight: "600", color: "#495057" }}>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {balanceSheetData.map((item, index) => (
                        <tr key={index} style={{ borderBottom: "1px solid #f1f3f5", background: index % 2 === 0 ? "#fff" : "#f8f9fa" }}>
                          <td style={{ padding: "12px 15px", textAlign: "center", color: "#6c757d" }}>{index + 1}</td>
                          <td style={{ padding: "12px 15px", fontWeight: "500", color: "#212529" }}>
                            {item.vendorName || item.customerName || "—"}
                          </td>
                          <td style={{ padding: "12px 15px", color: "#495057" }}>{item.billingMonth || "—"}</td>
                          <td style={{ padding: "12px 15px", color: "#495057" }}>
                            {item.lastSaleDate ? formatDateOnly(item.lastSaleDate) : "--"}
                          </td>
                          <td style={{ padding: "12px 15px", color: "#495057" }}>
                            {item.lastPaymentDate ? formatDateOnly(item.lastPaymentDate) : "--"}
                          </td>
                          <td
                            style={{
                              padding: "12px 15px",
                              textAlign: "right",
                              fontWeight: "600",
                              color: Number(item.balance) < 0 ? "#dc3545" : "#198754",
                            }}
                          >
                            {formatCurrency(item.balance)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: "#e9ecef", borderTop: "2px solid #dee2e6" }}>
                        <td colSpan="5" style={{ padding: "14px 15px", textAlign: "right", fontWeight: "600", fontSize: "15px" }}>Total Outstanding</td>
                        <td style={{ padding: "14px 15px", textAlign: "right", fontWeight: "700", fontSize: "16px", color: "#0d6efd", background: "#e9ecef" }}>
                          {formatCurrency(balanceSheetData.reduce((sum, item) => sum + Number(item.balance || 0), 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "60px 20px", color: "#6c757d", background: "#f8f9fa", borderRadius: "8px" }}>
                  {loadingBalanceSheet ? (
                    <>
                      <span className="spinner-border text-primary" role="status"></span>
                      <p style={{ marginTop: "15px" }}>Loading balance sheet...</p>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-file-spreadsheet" style={{ fontSize: "48px", opacity: "0.5" }}></i>
                      <p style={{ marginTop: "15px" }}>No data found. Select a month or click "Fetch".</p>
                    </>
                  )}
                </div>
              )}
              <div style={{ marginTop: "20px", paddingTop: "15px", borderTop: "1px solid #e9ecef", display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#6c757d" }}>
                <span>Total records: {balanceSheetData.length}</span>
                <span>Last updated: {new Date().toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="loading-state" style={{ textAlign: "center", padding: "40px" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p style={{ marginTop: "10px" }}>Loading ledger data...</p>
          </div>
        )}

        {!loading && ledgerData.monthLedgers && ledgerData.monthLedgers.length > 0 && (
          <div
            className="summary-section"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              padding: "20px",
              borderRadius: "10px",
              marginBottom: "25px",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            }}
          >
            <h3 style={{ marginTop: "0", marginBottom: "15px", display: "flex", alignItems: "center" }}>
              <i className="bi bi-bar-chart" style={{ marginRight: "10px" }}></i>Summary
            </h3>
            <div
              className="summary-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "20px",
              }}
            >
              <div className="summary-item">
                <div style={{ fontSize: "12px", opacity: "0.9" }}>Outstanding Balance</div>
                <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                  {formatCurrency(ledgerData.totalBalance)}
                </div>
              </div>
              <div className="summary-item">
                <div style={{ fontSize: "12px", opacity: "0.9" }}>Period</div>
                <div style={{ fontSize: "18px", fontWeight: "bold" }}>
                  {startMonth || "Start"} to {endMonth || "End"}
                </div>
              </div>
              <div className="summary-item">
                <div style={{ fontSize: "12px", opacity: "0.9" }}>Total Months</div>
                <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                  {ledgerData.monthLedgers.length}
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && ledgerData.monthLedgers && ledgerData.monthLedgers.length > 0 && (
          ledgerData.monthLedgers.map((monthData, idx) => (
            <MonthSection
              key={idx}
              monthData={monthData}
              expandedInvoices={expandedInvoices}
              onToggleInvoice={toggleInvoiceDetails}
            />
          ))
        )}

        {!loading && (!ledgerData.monthLedgers || ledgerData.monthLedgers.length === 0) && (
          <div
            style={{
              textAlign: "center",
              padding: "50px 20px",
              background: "#f8f9fa",
              borderRadius: "10px",
              border: "2px dashed #dee2e6",
              marginTop: "20px",
            }}
          >
            <i className="bi bi-journal-text" style={{ fontSize: "48px", color: "#adb5bd", marginBottom: "15px" }}></i>
            <h4 style={{ color: "#6c757d" }}>No Ledger Data Found</h4>
            <p style={{ color: "#6c757d", maxWidth: "500px", margin: "0 auto" }}>
              {vendorName
                ? `No transactions found for vendor "${vendorName}"${startMonth || endMonth ? ` between ${startMonth || "start"} and ${endMonth || "end"}` : ""}`
                : "Select a vendor and click 'Fetch Ledger' to view their transaction history"}
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default VendorLedger;